import React from 'react';
import productsData from '../data/products.json';

// Helper function to format category names
const formatCategoryName = (categoryId) => {
  return categoryId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ProductCatalog = () => {
  const handlePrint = () => {
    window.print();
  };

  // Group products by category
  const productsByCategory = productsData.products.reduce((acc, product) => {
    if (!acc[product.categoryId]) {
      acc[product.categoryId] = [];
    }
    acc[product.categoryId].push(product);
    return acc;
  }, {});

  return (
    <div className="product-catalog-container px-8 sm:px-12 md:px-20 lg:px-32 xl:px-48 2xl:px-64">
      <h1 className="text-4xl font-extrabold text-center my-10 text-gray-800">Product Catalog</h1>
      <div className="flex justify-center mb-12">
        <button
          onClick={handlePrint}
          className="print-button bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
        >
          Print Catalog
        </button>
      </div>
      
      <div className="product-list">
        {Object.entries(productsByCategory).map(([categoryId, products], index) => (
          
          <div key={categoryId} className="mb-12">
            {/* Category Header */}
            <div className="flex items-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 flex-shrink-0 pr-4">
                Category Name: {formatCategoryName(categoryId)} <span className="text-gray-500 text-xl font-normal">({index + 1}/{Object.keys(productsByCategory).length})</span>
              </h2>
              <div className="border-t border-gray-300 flex-grow ml-4"></div>
            </div>
            
            {/* Products Grid */}
            <div className="grid gap-6">
              {products.map((product, index) => (
                <div key={product.id} className="product-card flex flex-col sm:flex-row border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white">
                  <div className="product-image-container w-full sm:!w-[40%] flex items-center justify-center p-2 sm:p-4 bg-gray-50" style={{ flex: '0 0 40%' }}>
                    <img src={product.image} alt={product.name} className="w-full max-h-[100px] object-contain" />
                  </div>
                  <div className="product-details w-full sm:!w-[60%] p-4 sm:p-6" style={{ flex: '0 0 60%' }}>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 leading-snug">{product.name} <span className="text-gray-500 text-base font-normal">({index + 1}/{products.length})</span></h2>
                    <p className="text-gray-700 text-sm md:text-base mb-1"><strong className="font-semibold">Product Code:</strong> {product.id}</p>
                    <p className="text-gray-700 text-sm md:text-base mb-4"><strong className="font-semibold">SKU:</strong> {product.sku}</p>
                    <div className="product-description text-gray-700 text-sm md:text-base leading-relaxed">
                      <h3 className="font-semibold text-gray-800 mb-3 text-lg">Features:</h3>
                      <ul className="space-y-2">
                        {product.description.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-blue-600 mr-2 mt-1">•</span>
                            <span className="text-gray-800">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <style jsx="true">{`
        @media print {
          body * {
            visibility: hidden;
          }
          .product-catalog-container, .product-catalog-container * {
            visibility: visible;
            overflow: visible !important;
          }
          .product-catalog-container {
            position: static;
            left: auto;
            top: auto;
            width: auto;
            margin: 0;
            padding: 0;
          }
          .print-button {
            display: none;
          }
          .product-card {
            display: flex !important;
            flex-direction: row !important;
            border: 1px solid #e2e8f0 !important;
            margin-bottom: 1.5rem !important;
            page-break-inside: avoid;
            break-inside: avoid;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .product-image-container {
            width: 40% !important;
            flex: 0 0 40% !important;
            padding: 1rem !important;
            background-color: #f8fafc !important;
          }
          .product-image-container img {
            max-height: 100px !important;
            width: 100% !important;
          }
          .product-details {
            width: 60% !important;
            flex: 0 0 60% !important;
            max-width: 60% !important;
            flex-grow: 0 !important;
          }
          .product-details h2 {
            font-size: 1.1rem !important; /* Adjust for print */
          }
          .product-details p {
            font-size: 0.8rem !important; /* Adjust for print */
          }
          .product-details .product-description {
            font-size: 0.85rem !important; /* Adjust for print */
          }
          .product-details {
            flex-grow: 1 !important;
            padding: 1.5rem !important;
          }
          .product-details h2 {
            font-size: 1.25rem !important;
            margin-bottom: 0.5rem !important;
          }
          .product-details p {
            font-size: 0.875rem !important;
            margin-bottom: 0.25rem !important;
          }
          .product-details .product-description {
            font-size: 0.9rem !important;
            margin-top: 1rem !important;
          }
          .product-details .product-description h3 {
            font-size: 1.1rem !important;
            color: #1f2937 !important; /* gray-800 */
            font-weight: 600 !important;
            margin-bottom: 0.5rem !important;
          }
          .product-details .product-description ul {
            list-style: none !important;
            padding-left: 0 !important;
            margin: 0 !important;
          }
          .product-details .product-description li {
            margin-bottom: 0.5rem !important;
            line-height: 1.5 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductCatalog;
